import axios from 'axios';
import '../config.js';
import adminApi from './adminApi';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
  };
};

const base = (path) => `${global.config.url}${path}`;
const INTERNAL_MEMBERS_ENDPOINT_CANDIDATES = ['/members', '/internal/members'];

let resolvedInternalMembersEndpoint = null;
let internalMembersEndpointUnavailable = false;

const normalizeLookupItems = (items, idKeyCandidates, nameKeyCandidates) => {
  if (!Array.isArray(items)) return [];

  const pick = (obj, candidates) => {
    for (const key of candidates) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
    }
    return undefined;
  };

  return items
    .map((item) => {
      const rawId = pick(item, idKeyCandidates);
      const rawName = pick(item, nameKeyCandidates);
      const id = Number(rawId);
      const name = String(rawName || '').trim();

      if (!Number.isFinite(id) || !name) return null;
      return { id, name };
    })
    .filter(Boolean)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const toNonNegativeNumber = (value, fallback) => {
  const next = Number(value);
  return Number.isFinite(next) && next >= 0 ? next : fallback;
};

const toNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const toNullableString = (value) => {
  if (value === null || value === undefined) return null;
  const next = String(value).trim();
  return next ? next : null;
};

const resolveSupportAppProviderId = (explicitAppProviderId = null, useAppId = false) => {
  const storedAppProviderId = localStorage.getItem('appid');
  if (storedAppProviderId !== null) return storedAppProviderId;

  if (explicitAppProviderId !== null && explicitAppProviderId !== undefined) {
    return explicitAppProviderId;
  }

  return useAppId ? storedAppProviderId : null;
};

const normalizeMember = (member) => ({
  ...member,
  id: toNullableNumber(member && member.id),
  locationId: toNullableNumber(member && member.locationId),
  groupId: toNullableNumber(member && member.groupId),
  customerName: toNullableString(member && member.customerName),
  allowance: member && member.allowance !== undefined && member.allowance !== null ? member.allowance : '',
  temporal: Boolean(member && member.temporal),
  allowedNow: Boolean(member && member.allowedNow),
});

const buildMembersQuery = ({ locationId, search, appProviderId, page, pageSize } = {}) => {
  const params = {};
  const safeLocationId = toNullableNumber(locationId);
  const safeSearch = toNullableString(search);
  const safePage = toNonNegativeNumber(page, null);
  const safePageSize = toNonNegativeNumber(pageSize, null);
  const safeAppProviderId = resolveSupportAppProviderId(appProviderId);

  if (safeLocationId !== null) params.locationId = safeLocationId;
  if (safeSearch) params.search = safeSearch;
  if (safePage !== null) params.page = safePage;
  if (safePageSize !== null) params.pageSize = safePageSize;
  if (safeAppProviderId !== null) params.appProviderId = safeAppProviderId;

  return params;
};

const buildSupportMembersQuery = ({ locationId, search, appProviderId, page, pageSize, useAppId } = {}) => {
  let appId = appProviderId;
  if (appId === null && useAppId) {
    appId = localStorage.getItem('appid');
  }
  const safeAppProviderId = resolveSupportAppProviderId(appId, useAppId);

  return buildMembersQuery({ locationId, search, appProviderId: safeAppProviderId, page, pageSize });
};

const toPagedMembersResult = (response) => {
  const data = response && response.data && typeof response.data === 'object' ? response.data : {};
  const items = Array.isArray(data.items) ? data.items.map(normalizeMember) : [];
  const totalCount = toNonNegativeNumber(data.totalCount, 0);
  const page = toNonNegativeNumber(data.page, 0);
  const pageSize = toNonNegativeNumber(data.pageSize, 50);
  const totalPages = toNonNegativeNumber(data.totalPages, 0);

  return {
    items,
    totalCount,
    page,
    pageSize,
    totalPages,
  };
};

const buildMemberPayload = (member, lockedGroupId) => {
  const groupId = lockedGroupId !== null && lockedGroupId !== undefined
    ? Number(lockedGroupId)
    : toNullableNumber(member.groupId);

  return {
    name: String(member.name || '').trim(),
    surname: String(member.surname || '').trim(),
    memberId: String(member.memberId || '').trim(),
    locationId: toNullableNumber(member.locationId),
    groupId,
    type: toNullableString(member.type),
    allowance: member.allowance === '' || member.allowance === null || member.allowance === undefined
      ? null
      : member.allowance,
    initDate: toNullableString(member.initDate),
    endDate: toNullableString(member.endDate),
    extraFields: toNullableString(member.extraFields),
    comments: toNullableString(member.comments),
    temporal: Boolean(member.temporal),
    email: toNullableString(member.email),
    purpose: toNullableString(member.purpose),
    mobile: toNullableString(member.mobile),
  };
};

const buildCreateSupportMemberPayload = (member) => ({
  name: String(member && member.name ? member.name : '').trim(),
  surname: String(member && member.surname ? member.surname : '').trim(),
  locationId: toNullableNumber(member && member.locationId),
});

const buildUpdateSupportMemberPayload = (member) => ({
  name: String(member && member.name ? member.name : '').trim(),
  surname: String(member && member.surname ? member.surname : '').trim(),
  allowance: Boolean(member && member.allowance),
});

export const getSupportMembers = async (customerId, filters = {}) => {
  const response = await axios.get(base(`/support/members/${customerId}`), {
    headers: authHeaders(),
    params: buildSupportMembersQuery(filters),
  });

  return toPagedMembersResult(response);
};

const tryResolveInternalMembersEndpoint = async (filters = {}) => {
  if (resolvedInternalMembersEndpoint) {
    const response = await axios.get(base(resolvedInternalMembersEndpoint), {
      headers: authHeaders(),
      params: buildMembersQuery(filters),
    });
    return toPagedMembersResult(response);
  }

  if (internalMembersEndpointUnavailable) {
    return null;
  }

  for (const candidate of INTERNAL_MEMBERS_ENDPOINT_CANDIDATES) {
    try {
      const response = await axios.get(base(candidate), {
        headers: authHeaders(),
        params: buildMembersQuery(filters),
      });
      resolvedInternalMembersEndpoint = candidate;
      return toPagedMembersResult(response);
    } catch (error) {
      if (!error || !error.response || error.response.status !== 404) {
        throw error;
      }
    }
  }

  internalMembersEndpointUnavailable = true;
  return null;
};

export const getInternalMembers = async (filters = {}) => {
  const safeCustomerId = toNullableNumber(filters.customerId);

  if (safeCustomerId !== null) {
    return getSupportMembers(safeCustomerId, filters);
  }

  const globalResult = await tryResolveInternalMembersEndpoint(filters);
  if (globalResult) return globalResult;

  throw new Error('A global members endpoint is not available yet. Select a customer to load members with the current backend.');
};

export const getSupportMemberById = async (customerId, memberId, query = {}) => {
  const response = await axios.get(base(`/support/members/${customerId}/${memberId}`), {
    headers: authHeaders(),
    params: buildSupportMembersQuery(query),
  });

  return normalizeMember(response.data || {});
};

export const createSupportMember = async (customerId, member, lockedGroupId = null, query = {}) => {
  const response = await axios.post(
    base(`/support/members/${customerId}`),
    buildCreateSupportMemberPayload(member),
    {
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json;charset=UTF-8',
      },
      params: buildSupportMembersQuery(query),
    }
  );

  return normalizeMember(response.data || member);
};

export const updateSupportMember = async (customerId, memberId, member, lockedGroupId = null, query = {}) => {
  const response = await axios.put(
    base(`/support/members/${customerId}/${memberId}`),
    query && query.supportRestrictedUpdate ? buildUpdateSupportMemberPayload(member) : buildMemberPayload(member, lockedGroupId),
    {
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json;charset=UTF-8',
      },
      params: buildSupportMembersQuery(query),
    }
  );

  return normalizeMember(response.data || member);
};

export const deleteSupportMember = async (customerId, memberId, query = {}) => {
  const response = await axios.delete(base(`/support/members/${customerId}/${memberId}`), {
    headers: authHeaders(),
    params: buildSupportMembersQuery(query),
  });

  return response.data;
};

export const getSupportLocations = async (customerId, appProviderId = null) => {
  let path = `/support/locations/${customerId}/false`;
  const safeAppProviderId = resolveSupportAppProviderId(appProviderId);
  if (safeAppProviderId !== null) {
    path = `${path}?appProviderId=${safeAppProviderId}`;
  }

  const response = await axios.get(base(path), {
    headers: authHeaders(),
  });

  return normalizeLookupItems(response.data, ['id', 'locationId'], ['name', 'locationName']);
};

export const getInternalLocationsByCustomer = async (customerId) => {
  const response = await axios.get(base(`/locations/ByCustomer/${customerId}`), {
    headers: authHeaders(),
  });

  return normalizeLookupItems(response.data, ['id', 'locationId'], ['name', 'locationName']);
};

export const getInternalLocationLookup = async (customerIds) => {
  const uniqueCustomerIds = Array.from(new Set((customerIds || []).map((id) => Number(id)).filter(Number.isFinite)));
  const responses = await Promise.all(
    uniqueCustomerIds.map(async (customerId) => {
      const locations = await getInternalLocationsByCustomer(customerId);
      return locations.map((location) => ({
        ...location,
        customerId,
        label: `${location.name} (${customerId})`,
      }));
    })
  );

  return responses.flat();
};

export const getAllMembers = async ({ customerId, locationId, search, appProviderId } = {}) => {
  const safeCustomerId = toNullableNumber(customerId);
  const filters = { locationId, search, appProviderId };

  if (safeCustomerId !== null) {
    const result = await getSupportMembers(safeCustomerId, filters);
    return result.items;
  }

  const customers = await adminApi.getCustomersLookup();
  const results = await Promise.all(
    customers.map((customer) => getSupportMembers(customer.id, filters))
  );

  return results.flatMap((result) => result.items || []);
};

const membersApi = {
  buildSupportMembersQuery,
  getSupportMembers,
  getInternalMembers,
  getSupportMemberById,
  createSupportMember,
  updateSupportMember,
  deleteSupportMember,
  getSupportLocations,
  getInternalLocationsByCustomer,
  getInternalLocationLookup,
  getAllMembers,
};

export default membersApi;
